import { useRef } from "react";
import { uniqueId } from "lodash";
import { t } from "@/i18n-js/instance";
import type { MarketingEmailTemplate } from "@/react/types/MarketingEmailTemplates";
import { Form } from "@circle-react-uikit/Form";
import { ConfirmationModal } from "../../ModalsV2/ConfirmationModal";
import { CreateTemplateContent } from "../UpsertTemplateModal/CreateTemplateContent";
import { createTemplateValidationSchema } from "../UpsertTemplateModal/validationSchema";
import { useUpdateTemplate } from "./hooks/useUpdateTemplate";

const localeNamespace = "settings.emails.templates";

export function RenameTemplateModal({
  template,
  isOpen,
  onClose,
}: {
  template: MarketingEmailTemplate;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { current: formId } = useRef(uniqueId("template_name_"));
  const formInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: updateTemplate, isLoading } = useUpdateTemplate();
  const defaultValues = { name: template.name };

  const handleSubmit = async (values: typeof defaultValues) => {
    await updateTemplate({
      id: template.id,
      body: { name: values.name, template_type: template.template_type },
    });
    onClose();
  };

  return (
    <ConfirmationModal
      formId={formId}
      isOpen={isOpen}
      disabled={isLoading}
      onClose={onClose}
      title={t([localeNamespace, "rename_template"])}
      confirmVariant="circle"
      initialFocusRef={formInputRef}
      confirmText={
        isLoading
          ? t([localeNamespace, "saving"])
          : t([localeNamespace, "save"])
      }
    >
      <Form
        id={formId}
        defaultValues={defaultValues}
        validationSchema={createTemplateValidationSchema}
        onSubmit={handleSubmit}
      >
        <CreateTemplateContent nameInputRef={formInputRef} />
      </Form>
    </ConfirmationModal>
  );
}
